import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { SEOHead } from '../components';
import { PATiFollowTheClockDiagram, PATiSessionOrchestrationDiagram } from '../components/executive';
import {
  ArrowLeft,
  ArrowRight,
  Shield,
  Globe2,
  Brain,
  Lock,
  Clock,
  CheckCircle2,
  Layers,
  Target,
  TrendingUp,
  Building2,
  Users,
  Zap,
} from 'lucide-react';

const pillars = [
  {
    icon: Lock,
    title: 'Principal Custody',
    description: 'Models, workflows, and execution logic remain inside your perimeter. Retail SaaS is the wrong comparator.',
    accent: 'indigo',
  },
  {
    icon: Globe2,
    title: 'Follow-The-Clock',
    description: 'US, European, and Asian sessions on one architecture—premarket through reconciliation, scoped so markets never bleed into each other.',
    accent: 'cyan',
  },
  {
    icon: Shield,
    title: 'Risk As Architecture',
    description: 'Stops, gain protection, and portfolio supervision are native services—not settings you remember to toggle after the fact.',
    accent: 'emerald',
  },
  {
    icon: Brain,
    title: 'Agentic Counsel',
    description: 'Structured decision roles with consensus, confidence gates, and veto paths. Judgment under uncertainty, not a stock-picker gimmick.',
    accent: 'violet',
  },
];

const differentiators = [
  {
    icon: Layers,
    label: 'System Of Record And Action',
    detail: 'Decisioning, execution, reconciliation, and cleanup in one stack—the difference between a demo and an operating system.',
  },
  {
    icon: Target,
    label: 'Fail-Closed Readiness',
    detail: 'Broker health, schema integrity, and session gates must clear before capital moves. Ambiguous state does not trade.',
  },
  {
    icon: Clock,
    label: 'Session-Scoped State',
    detail: 'Workflow memory, targets, and controls stay inside the market they belong to. No accidental cross-border contamination.',
  },
  {
    icon: Shield,
    label: 'Hands-Off, Not Ungoverned',
    detail: 'The desk steps back from keystrokes—not from accountability. Supervision runs through the cycle, not around it.',
  },
  {
    icon: TrendingUp,
    label: 'Consistency Of Process',
    detail: 'We sell repeatability of orchestration and risk discipline—not promises about returns. That framing holds up with counsel.',
  },
  {
    icon: Zap,
    label: 'Private Inference Path',
    detail: 'Assistance without shipping proprietary context through public model APIs. Your signals stay yours.',
  },
];

const personas = [
  {
    title: 'Family Office Principals',
    description: 'Capital that demands named ownership, defensible infrastructure, and a narrative LPs and counsel can follow.',
    accent: 'indigo',
  },
  {
    title: 'Boutique Desk Heads',
    description: 'A productized layer between mass-market terminals and a bespoke institutional build—without surrendering workflow IP.',
    accent: 'cyan',
  },
  {
    title: 'Global Operators',
    description: 'Follow-the-clock coverage when the mandate crosses time zones, asset classes, and listing currencies.',
    accent: 'emerald',
  },
  {
    title: 'Infrastructure Principals',
    description: 'White-label or internal deployment where extensibility and auditability outweigh onboarding speed.',
    accent: 'violet',
  },
];

const marketPosition = [
  {
    title: 'Retail SaaS',
    line: 'Fast trial, low monthly fee, thin workflows—and vendor custody of the logic that actually matters.',
  },
  {
    title: 'Institutional Build',
    line: 'High ceiling, fragmented ownership, elevated sustainment. Rarely AI-native end to end.',
  },
  {
    title: 'Cloud-Only AI',
    line: 'Convenience that routes operating context through someone else\'s inference bill.',
  },
  {
    title: 'The Opening',
    line: 'Principals want automation without outsourcing execution logic—or operating across a US-only terminal.',
  },
];

const accentText = {
  indigo: 'text-indigo-400',
  cyan: 'text-cyan-400',
  emerald: 'text-emerald-400',
  violet: 'text-violet-400',
};

const accentBorder = {
  indigo: 'border-l-indigo-500',
  cyan: 'border-l-cyan-500',
  emerald: 'border-l-emerald-500',
  violet: 'border-l-violet-500',
};

const PATi = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div className={isDark ? 'bg-[#070b14] text-slate-100' : 'bg-white text-slate-800'}>
      <SEOHead
        title="PATi — Private Algorithmic Trading Int'l"
        description="Operator-sovereign trading infrastructure: follow-the-clock equities and options orchestration, private inference, broker execution, and embedded risk—for principals who own the stack."
        type="website"
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: isDark
              ? 'radial-gradient(ellipse 80% 55% at 90% 0%, rgba(45,212,191,0.2), transparent 50%), radial-gradient(ellipse 60% 45% at 5% 95%, rgba(37,99,235,0.18), transparent 48%)'
              : 'radial-gradient(ellipse 80% 55% at 90% 0%, rgba(45,212,191,0.12), transparent 50%), radial-gradient(ellipse 60% 45% at 5% 95%, rgba(37,99,235,0.08), transparent 48%)',
          }}
        />
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.03] pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
          <Link
            to="/"
            className={`inline-flex items-center gap-2 text-sm font-medium mb-10 ${isDark ? 'text-cyan-400 hover:text-cyan-300' : 'text-cyan-700 hover:text-cyan-900'}`}
          >
            <ArrowLeft size={16} aria-hidden />
            Shared Oxygen
          </Link>

          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="space-y-6 max-w-xl">
              <h1 className="text-4xl lg:text-5xl font-bold tracking-tight">PATi</h1>
              <p className={`text-sm font-medium tracking-wide -mt-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Private Algorithmic Trading Int&apos;l
              </p>

              <span className="inline-block text-xs font-bold tracking-wide px-4 py-2 rounded-full border border-teal-500/45 bg-teal-500/10 text-teal-300">
                Operator-Sovereign Trading Infrastructure
              </span>

              <h2 className="text-3xl lg:text-[2.35rem] font-bold leading-[1.12] tracking-tight">
                When The Mandate Spans Sessions, Asset Classes, And Time Zones—Run One Owned System.
              </h2>

              <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                PATi is not another subscription that picks tickers. It is private infrastructure for
                follow-the-clock equities and options—agentic counsel, broker-connected execution, and
                risk discipline wired into the same operating cycle.
              </p>

              <p className={`text-base leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Hands-off for the operator. Governed for the principal. Built for the gap between
                mass-market SaaS and a fragmented institutional stack.
              </p>

              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/contact?subject=PATi%20Executive%20Briefing"
                  className="group inline-flex items-center gap-2 px-8 py-4 bg-teal-500 hover:bg-teal-600 text-slate-900 font-bold rounded-xl transition-colors"
                >
                  Request An Executive Briefing
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/contact?subject=PATi%20Architecture%20Review"
                  className={`inline-flex items-center gap-2 px-8 py-4 font-semibold rounded-xl border transition-colors ${
                    isDark
                      ? 'border-slate-600 text-slate-200 hover:border-teal-500 hover:text-teal-300'
                      : 'border-slate-300 text-slate-700 hover:border-teal-600 hover:text-teal-800'
                  }`}
                >
                  Architecture Review
                </Link>
              </div>
            </div>

            <div className="w-full lg:max-w-none">
              <PATiFollowTheClockDiagram theme={theme} />
            </div>
          </div>
        </div>
      </section>

      {/* Market position */}
      <section className={`py-16 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Market Position</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-3">Between Retail Convenience And Institutional Fragmentation</h3>
          <p className={`max-w-3xl mb-10 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Value accrues to whoever holds the models, the workflows, and the execution logic.
            PATi is priced and architected for principals who intend to keep that custody.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {marketPosition.map((item) => (
              <div
                key={item.title}
                className={`p-6 rounded-xl ${isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-slate-50 border border-slate-200'}`}
              >
                <h4 className="font-bold text-lg mb-2">{item.title}</h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{item.line}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className={`py-20 ${isDark ? 'bg-slate-950/50' : 'bg-slate-50'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Investment Thesis</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-4">Vertical Integration Across Hard Surfaces</h3>
          <p className={`max-w-2xl mb-12 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            Local inference. Follow-the-clock orchestration. Broker execution. Embedded risk.
            One stack—because the moat is integration, not a single feature on a slide.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {pillars.map(({ icon: Icon, title, description, accent }) => (
              <div
                key={title}
                className={`p-6 rounded-xl border ${isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}
              >
                <Icon className={`w-6 h-6 mb-3 ${accentText[accent]}`} />
                <h4 className={`text-base font-bold mb-2 ${accentText[accent]}`}>{title}</h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Session orchestration */}
      <section className="py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-12 items-start mb-10">
            <div className="lg:col-span-2 space-y-5">
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Follow-The-Clock</p>
              <h3 className="text-2xl lg:text-3xl font-bold leading-tight">
                The Desk Follows The Sun. The Mandate Stays Intact.
              </h3>
              <p className={`text-lg leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                New York opens. London takes the baton. Tokyo and Hong Kong carry it forward.
                The continuous lane never goes dark—each handoff supervised, each venue scoped,
                each instrument class where your charter allows it.
              </p>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                Equities and listed options in the Americas. International listings in local currency.
                Pacific sessions with lunch-aware discipline. One architecture holds the thread—
                capital does not reset when the clock changes geography.
              </p>
            </div>
            <div className="lg:col-span-3">
              <PATiSessionOrchestrationDiagram theme={theme} />
            </div>
          </div>
        </div>
      </section>

      {/* Differentiators */}
      <section className={`py-20 ${isDark ? 'bg-slate-900/40' : 'bg-slate-100'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Operating Discipline</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-12">Infrastructure Designed For Repeatable Operation</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentiators.map(({ icon: Icon, label, detail }) => (
              <div key={label} className="flex gap-4">
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${
                    isDark ? 'bg-teal-500/15 text-teal-400' : 'bg-teal-50 text-teal-700'
                  }`}
                >
                  <Icon size={20} />
                </div>
                <div>
                  <h4 className="font-bold mb-1">{label}</h4>
                  <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Personas */}
      <section className={`py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Principal Fit</p>
          <h3 className="text-2xl lg:text-3xl font-bold mb-10">Capital That Intends To Own The Stack</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {personas.map((persona) => (
              <div
                key={persona.title}
                className={`p-6 rounded-xl border-l-4 ${accentBorder[persona.accent]} ${
                  isDark ? 'bg-white/[0.03] border border-white/10' : 'bg-slate-50 border border-slate-200'
                }`}
              >
                <h4 className="font-bold mb-2">{persona.title}</h4>
                <p className={`text-sm leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  {persona.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Deployment */}
      <section className={`py-20 ${isDark ? 'bg-slate-950/80' : 'bg-slate-50'}`}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400 mb-3">Commercial Posture</p>
              <h3 className="text-2xl lg:text-3xl font-bold mb-6">Infrastructure Economics, Not Subscription Arithmetic</h3>
              <p className={`mb-8 leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>
                PATi does not win on beating retail SaaS monthly fees. It wins where implementation depth,
                private deployment, and optional managed operations align with how serious capital actually buys capability.
              </p>
              <ul className="space-y-4">
                {[
                  'Self-hosted or managed—principal chooses the operating model',
                  'International session handoff on one owned system',
                  'Broker readiness and schema gates before live capital deploys',
                  'Risk services native to the cycle—not a separate risk dashboard',
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-teal-400' : 'text-teal-600'}`} />
                    <span className={`text-sm leading-relaxed ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div
              className={`p-8 rounded-2xl border space-y-6 ${
                isDark ? 'bg-slate-900/60 border-white/10' : 'bg-white border-slate-200 shadow-md'
              }`}
            >
              <div className="flex items-center gap-3">
                <Building2 className={`w-6 h-6 ${isDark ? 'text-indigo-400' : 'text-indigo-600'}`} />
                <span className="font-bold">Family Office &amp; Boutique Desk</span>
              </div>
              <div className="flex items-center gap-3">
                <Users className={`w-6 h-6 ${isDark ? 'text-teal-400' : 'text-teal-600'}`} />
                <span className="font-bold">Multi-Operator Governance</span>
              </div>
              <div className="flex items-center gap-3">
                <Globe2 className={`w-6 h-6 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                <span className="font-bold">US · Europe · Asia · Always-On</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className={`py-20 border-t ${isDark ? 'border-slate-800' : 'border-slate-200'}`}>
        <div className="max-w-4xl mx-auto px-6 text-center space-y-6">
          <h3 className={`text-3xl lg:text-4xl font-bold leading-tight ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <span className="block">Not AI That Picks Stocks.</span>
            <span className={`block mt-3 ${isDark ? 'text-teal-400' : 'text-teal-600'}`}>
              Infrastructure That Runs The Desk.
            </span>
          </h3>
          <p className={`text-lg max-w-2xl mx-auto leading-relaxed ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
            See how session handoff, supervised equities and options execution, and fail-closed
            readiness gates hold together as one operating cycle—not a collection of disconnected tools.
          </p>
          <Link
            to="/contact?subject=PATi%20Conversation"
            className="inline-flex items-center gap-2 px-10 py-4 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl transition-colors"
          >
            Start The Conversation
            <ArrowRight className="w-5 h-5" />
          </Link>
          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
            PATi — A Product Of Shared Oxygen, LLC
          </p>
        </div>
      </section>
    </div>
  );
};

export default PATi;
